import React from 'react';

// @mui
import { alpha, styled } from '@mui/material/styles';
import { Card, Stack, Tooltip, tooltipClasses, TooltipProps, Typography } from '@mui/material';
// utils
// import { fShortenNumber } from 'src/utils/formatNumber';
// theme
import { ColorSchema } from 'src/theme/palette';
// components
import Iconify from 'src/components/Iconify';
// icons
import { AiOutlineInfoCircle } from 'react-icons/ai';

// ----------------------------------------------------------------------

const RootStyle = styled(Card)(({ theme }) => ({
  boxShadow: 'none',
  textAlign: 'center',
  padding: theme.spacing(5, 0),
}));

const IconWrapperStyle = styled('div')(({ theme }) => ({
  margin: 'auto',
  display: 'flex',
  borderRadius: '50%',
  alignItems: 'center',
  width: theme.spacing(8),
  height: theme.spacing(8),
  justifyContent: 'center',
  marginBottom: theme.spacing(3),
}));

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#ffffff',
    color: 'rgba(0, 0, 0, 0.87)',
    maxWidth: 220,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
  },
}));

// ----------------------------------------------------------------------

type DashboardAnalyticsSummaryProps = {
  title: string;
  stat: number | string;
  icon: string;
  color?: ColorSchema;
  description?: React.ReactChild | React.ReactFragment | React.ReactPortal;
  onTooltipHover?: () => void;
};

export default function DashboardAnalyticsSummary({
  title,
  stat,
  icon,
  color = 'primary',
  description,
  onTooltipHover,
}: DashboardAnalyticsSummaryProps) {
  const InfoTooltip: React.FC<{}> = ({ children }) => {
    if (!description) {
      return <>{children}</>;
    }

    return (
      <HtmlTooltip
        title={description}
        onOpen={(event) => {
          if (onTooltipHover) {
            onTooltipHover();
          }
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="center" gap={1}>
          <>{children}</>
          <AiOutlineInfoCircle size={18} />
        </Stack>
      </HtmlTooltip>
    );
  };

  return (
    <RootStyle
      sx={{
        color: (theme) => theme.palette[color].darker,
        bgcolor: (theme) => theme.palette[color].lighter,
      }}
    >
      <IconWrapperStyle
        sx={{
          color: (theme) => theme.palette[color].dark,
          backgroundImage: (theme) =>
            `linear-gradient(135deg, ${alpha(theme.palette[color].dark, 0)} 0%, ${alpha(
              theme.palette[color].dark,
              0.24
            )} 100%)`,
        }}
      >
        <Iconify icon={icon} width={24} height={24} />
      </IconWrapperStyle>
      <Typography variant="h3">{stat}</Typography>

      <InfoTooltip>
        <Typography variant="subtitle2" sx={{ opacity: 0.72 }}>
          {title}
        </Typography>
      </InfoTooltip>
    </RootStyle>
  );
}
